"use client";
import Link from "next/link";
import { motion } from "framer-motion";
import Image from '@/components/ui/MediaImage';
import type { BlogPostCardViewModel } from "@/types/blog";
import { transformMediaUrl } from "@/lib/media-url-transformer";

export type PostCardData = BlogPostCardViewModel;

interface PostCardProps {
    post: PostCardData;
    index: number;
    isInView: boolean;
}

export default function PostCard({post,index,isInView}: PostCardProps) {
    const variant = post.variant ?? "primary";
    const imageAspectClass = variant === "latest" ? "aspect-[400/233]" : "aspect-[900/464]";
    const resolvedSrc = transformMediaUrl(post.src, null) || post.src;
    return (
        <motion.article
            initial={{ opacity: 0, y: 60, scale: 0.95 }}
            animate={{
                opacity: isInView ? 1 : 0,
                y: isInView ? 0 : 60,
                scale: isInView ? 1 : 0.95
            }}
            transition={{
                delay: isInView ? index * 0.15 : 0,
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1]
            }}
            className="group h-full"
            data-gjs-editable="false"
            data-gjs-draggable="false"
            data-gjs-droppable="false"
            data-gjs-removable="false"
            data-gjs-selectable="false"
        >
            <Link
                href={post.href}
                className="flex flex-col h-full min-h-[380px] bg-white font-sans rounded-xl overflow-hidden shadow-post-card shadow-post-card-hover border border-neutral-200/80 hover:border-neutral-200 transition-all duration-300"
            >
                <motion.div
                    className={`relative w-full flex-shrink-0 overflow-hidden ${imageAspectClass}`}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.4 }}
                >
                    <motion.div
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="w-full h-full relative"
                    >
                        
                        <Image
                            src={resolvedSrc}
                            alt={post.alt}
                            className="object-cover"
                            fill
                            sizes={variant === "latest" ? "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" : "(min-width: 1024px) 66vw, 100vw"}
                            unoptimized={resolvedSrc.startsWith('/api/media/proxy/')}
                        />

                        <div className="absolute top-5 left-5 w-[50px] py-2 bg-alsero-first rounded-sm font-semibold text-white text-sm flex justify-center">
                            <span className="w-[28px] text-center">{post.date}</span>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"
                    />

                </motion.div>
                <div className="flex flex-col flex-1 min-h-0 px-0">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: isInView ? 1 : 0, x: isInView ? 0 : -20 }}
                        transition={{ delay: isInView ? index * 0.15 + 0.2 : 0, duration: 0.5 }}
                        className={`flex items-center text-sm text-[#020626] mt-4 mb-3 px-3 ${variant === 'primary' ? 'justify-start gap-10' : 'justify-between'}`}>
                        <span className="flex items-center gap-3">
                            <svg width="14" height="17" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M8.5 9.56812C11.5312 9.56812 14 12.0369 14 15.0681C14 15.6306 13.5312 16.0681 13 16.0681H1C0.4375 16.0681 0 15.6306 0 15.0681C0 12.0369 2.4375 9.56812 5.5 9.56812H8.5ZM1.5 14.5681H12.4688C12.2188 12.5994 10.5312 11.0681 8.5 11.0681H5.5C3.4375 11.0681 1.75 12.5994 1.5 14.5681ZM7 8.06812C4.78125 8.06812 3 6.28687 3 4.06812C3 1.88062 4.78125 0.0681152 7 0.0681152C9.1875 0.0681152 11 1.88062 11 4.06812C11 6.28687 9.1875 8.06812 7 8.06812ZM7 1.56812C5.59375 1.56812 4.5 2.69312 4.5 4.06812C4.5 5.47437 5.59375 6.56812 7 6.56812C8.375 6.56812 9.5 5.47437 9.5 4.06812C9.5 2.69312 8.375 1.56812 7 1.56812Z" fill="#E31E24"/></svg>
                            <span>{post.author}</span>
                        </span>
                        <span className="flex items-center gap-1">
                            <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                <path d="M7.76562 0.019165C11.5117 0.019165 14.5469 3.05432 14.5469 6.80042C14.5469 10.5465 11.5117 13.5817 7.76562 13.5817C4.01953 13.5817 0.984375 10.5465 0.984375 6.80042C0.984375 3.05432 4.01953 0.019165 7.76562 0.019165ZM7.76562 12.2692C10.7734 12.2692 13.2344 9.83557 13.2344 6.80042C13.2344 3.7926 10.7734 1.33167 7.76562 1.33167C4.73047 1.33167 2.29688 3.7926 2.29688 6.80042C2.29688 9.83557 4.73047 12.2692 7.76562 12.2692ZM9.43359 9.42542L7.10938 7.7301C7.02734 7.67542 7 7.56604 7 7.48401V2.97229C7 2.80823 7.13672 2.64417 7.32812 2.64417H8.20312C8.36719 2.64417 8.53125 2.80823 8.53125 2.97229V6.8551L10.3359 8.19495C10.5 8.30432 10.5273 8.49573 10.418 8.65979L9.89844 9.34338C9.78906 9.50745 9.59766 9.53479 9.43359 9.42542Z" fill="#E31E24"/>
                            </svg>
                            <span>{post.readTime}</span>
                        </span>
                    </motion.div>
                    <div className="px-3 pb-4 flex flex-col flex-1 min-h-0">
                        <motion.h2
                            className="text-xl font-semibold text-[#020626] mb-3 leading-snug line-clamp-2 group-hover:text-alsero-text-dark transition-colors duration-300"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 20 }}
                            transition={{ delay: isInView ? index * 0.15 + 0.3 : 0, duration: 0.5 }}
                        >
                            {post.title}
                        </motion.h2>

                        <p className="text-base text-alsero-light-gray line-clamp-3 leading-relaxed">{post.description}</p>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: isInView ? 1 : 0 }}
                            transition={{ delay: isInView ? index * 0.15 + 0.4 : 0, duration: 0.5 }}
                            className="flex items-center gap-2 group mt-auto pt-6"
                        >
                            <span className="group-hover:text-alsero-text-dark text-sm text-[#132047] tracking-[20%] text-transition-colors duration-300">
                                READ MORE
                            </span>
                            <div className="flex items-center">
                                <svg width="38" height="8" viewBox="0 0 38 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative fill-alsero-dark-gray group-hover:fill-alsero-soft-white" aria-hidden="true" focusable="false">
                                    <path d="M37.0556 4.13675C37.2509 3.94149 37.2509 3.62491 37.0556 3.42965L33.8736 0.247666C33.6784 0.0524042 33.3618 0.0524042 33.1665 0.247666C32.9713 0.442929 32.9713 0.759511 33.1665 0.954773L35.9949 3.7832L33.1665 6.61163C32.9713 6.80689 32.9713 7.12347 33.1665 7.31873C33.3618 7.514 33.6784 7.514 33.8736 7.31873L37.0556 4.13675ZM0.697266 4.2832L36.702 4.2832L36.702 3.2832L0.697266 3.2832L0.697266 4.2832Z"/>
                                </svg>
                                <span className="group-hover:bg-alsero-first rounded-full w-7 h-7 flex -ml-3.5 border border-[#E8F2F9] transition-all duration-300 ease-in-out"></span>
                            </div>

                        </motion.div>
                    </div>
                </div>
            </Link>
        </motion.article>
    )
}